<template>
  <div class="merges-modal-overlay" @click.self="$emit('close')">
    <div class="merges-modal">
      <div class="merges-modal__header">
        <h2 class="merges-modal__title">
          <i class="mdi mdi-link-variant"></i> Manage Merged Users
        </h2>
        <button class="merges-modal__close" @click="$emit('close')">
          <i class="mdi mdi-close"></i>
        </button>
      </div>

      <div class="merges-modal__body">

        <!-- Create new merge -->
        <section class="merges-modal__section">
          <h3 class="merges-modal__section-title">Create a new merge</h3>
          <p class="merges-modal__hint">
            Link two users who represent the same person. Any email from either account will log in as the primary.
          </p>

          <div class="merges-modal__form">
            <b-field label="User A">
              <b-select v-model="form.userAId" placeholder="Select user" expanded>
                <option
                  v-for="u in selectableUsers"
                  :key="u._id"
                  :value="u._id"
                  :disabled="u._id === form.userBId"
                >
                  {{ u.firstName }} {{ u.lastName }} — {{ u.email }}
                </option>
              </b-select>
            </b-field>

            <b-field label="User B">
              <b-select v-model="form.userBId" placeholder="Select user" expanded>
                <option
                  v-for="u in selectableUsers"
                  :key="u._id"
                  :value="u._id"
                  :disabled="u._id === form.userAId"
                >
                  {{ u.firstName }} {{ u.lastName }} — {{ u.email }}
                </option>
              </b-select>
            </b-field>

            <b-field label="Primary (canonical) account">
              <b-select v-model="form.primaryUserId" placeholder="Select primary" expanded :disabled="!form.userAId || !form.userBId">
                <option v-if="form.userAId" :value="form.userAId">{{ labelFor(form.userAId) }}</option>
                <option v-if="form.userBId" :value="form.userBId">{{ labelFor(form.userBId) }}</option>
              </b-select>
            </b-field>

            <button
              class="merges-modal__create-btn"
              :disabled="!canCreate || creating"
              @click="createMerge"
            >
              <i class="mdi mdi-plus"></i>
              {{ creating ? 'Creating…' : 'Create Merge' }}
            </button>
          </div>
        </section>

        <hr class="merges-modal__divider" />

        <!-- Existing merges -->
        <section class="merges-modal__section">
          <h3 class="merges-modal__section-title">
            Existing merges
            <span v-if="!loadingMerges" class="merges-modal__count">{{ merges.length }}</span>
          </h3>

          <div v-if="loadingMerges" class="merges-modal__loading">
            <b-loading :active="true" :is-full-page="false" />
          </div>
          <div v-else-if="merges.length === 0" class="merges-modal__empty">
            No merged user records yet.
          </div>
          <div v-else class="merges-modal__list">
            <div
              v-for="mu in merges"
              :key="mu._id"
              class="merges-modal__item"
            >
              <div class="merges-modal__item-users">
                <div
                  v-for="u in mu.users"
                  :key="u._id"
                  class="merges-modal__item-user"
                  :class="{ 'is-primary': u._id === mu.primaryUser._id }"
                >
                  <i class="mdi" :class="u._id === mu.primaryUser._id ? 'mdi-star' : 'mdi-account'"></i>
                  <span class="merges-modal__item-name">{{ u.firstName }} {{ u.lastName }}</span>
                  <span class="merges-modal__item-email">{{ u.email }}</span>
                  <span v-if="u._id === mu.primaryUser._id" class="merges-modal__primary-tag">primary</span>
                </div>
              </div>
              <button
                class="merges-modal__delete-btn"
                :disabled="deletingId === mu._id"
                @click="deleteMerge(mu._id)"
              >
                <i class="mdi mdi-link-variant-off"></i>
                {{ deletingId === mu._id ? 'Unlinking…' : 'Unlink' }}
              </button>
            </div>
          </div>
        </section>

      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ManageMergesModal',
  props: {
    companyId: { type: String, required: true },
    authHeader: { type: Object, required: true },
    users: { type: Array, default: () => [] },
  },
  emits: ['close', 'done'],
  data() {
    return {
      merges: [],
      loadingMerges: false,
      creating: false,
      deletingId: null,
      form: {
        userAId: null,
        userBId: null,
        primaryUserId: null,
      },
    };
  },
  computed: {
    apiBase() {
      return process.env.VUE_APP_API_URL;
    },
    // Only show users who aren't already in a merge
    selectableUsers() {
      const mergedIds = new Set();
      for (const mu of this.merges) {
        for (const u of mu.users) mergedIds.add(u._id);
      }
      return this.users.filter(u => !mergedIds.has(u._id));
    },
    canCreate() {
      return this.form.userAId && this.form.userBId && this.form.primaryUserId
        && this.form.userAId !== this.form.userBId;
    },
  },
  mounted() {
    this.loadMerges();
  },
  methods: {
    async loadMerges() {
      this.loadingMerges = true;
      try {
        const res = await axios.get(
          `${this.apiBase}/superuser/companies/${this.companyId}/merged-users`,
          { headers: this.authHeader }
        );
        this.merges = res.data;
      } catch {
        this.$buefy.toast.open({ message: 'Failed to load merges.', type: 'is-danger' });
      } finally {
        this.loadingMerges = false;
      }
    },

    labelFor(userId) {
      const u = this.users.find(u => u._id === userId);
      if (!u) return userId;
      return `${u.firstName} ${u.lastName} — ${u.email}`;
    },

    async createMerge() {
      if (!this.canCreate || this.creating) return;
      this.creating = true;
      try {
        await axios.post(
          `${this.apiBase}/superuser/companies/${this.companyId}/merged-users`,
          {
            userAId: this.form.userAId,
            userBId: this.form.userBId,
            primaryUserId: this.form.primaryUserId,
          },
          { headers: this.authHeader }
        );
        this.form = { userAId: null, userBId: null, primaryUserId: null };
        this.$buefy.toast.open({ message: 'Users merged successfully.', type: 'is-success' });
        await this.loadMerges();
        this.$emit('done');
      } catch (err) {
        const msg = err?.response?.data?.message || 'Failed to create merge.';
        this.$buefy.toast.open({ message: msg, type: 'is-danger' });
      } finally {
        this.creating = false;
      }
    },

    async deleteMerge(mergedUserId) {
      if (this.deletingId) return;
      this.deletingId = mergedUserId;
      try {
        await axios.delete(
          `${this.apiBase}/superuser/merged-users/${mergedUserId}`,
          { headers: this.authHeader }
        );
        this.$buefy.toast.open({ message: 'Merge removed.', type: 'is-success' });
        await this.loadMerges();
        this.$emit('done');
      } catch (err) {
        const msg = err?.response?.data?.message || 'Failed to remove merge.';
        this.$buefy.toast.open({ message: msg, type: 'is-danger' });
      } finally {
        this.deletingId = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.merges-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
}

.merges-modal {
  background: $color-surface;
  border-radius: 1rem;
  box-shadow: $shadow-lg;
  width: 100%;
  max-width: 620px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.merges-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid $color-border;
  flex-shrink: 0;
}

.merges-modal__title {
  font-size: 1.15rem;
  font-weight: 800;
  color: $color-text;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  i { color: $color-primary; }
}

.merges-modal__close {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.2rem;
  color: $color-text-muted;
  padding: 0.25rem;

  &:hover { color: $color-text; }
}

.merges-modal__body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.merges-modal__section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.merges-modal__section-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: $color-text;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.merges-modal__count {
  background: $color-surface-muted;
  color: $color-text-muted;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.1rem 0.45rem;
  border-radius: $radius-pill;
}

.merges-modal__hint {
  font-size: 0.85rem;
  color: $color-text-muted;
  margin: 0;
}

.merges-modal__form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.merges-modal__create-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: $color-primary;
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  padding: 0.55rem 1.1rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  align-self: flex-start;
  transition: filter 0.15s;

  i { font-size: 1rem; }

  &:hover:not(:disabled) { filter: brightness(1.08); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

.merges-modal__divider {
  border: none;
  border-top: 1px solid $color-border;
  margin: 0;
}

.merges-modal__loading {
  position: relative;
  min-height: 60px;
}

.merges-modal__empty {
  font-size: 0.9rem;
  color: $color-text-subtle;
  padding: 0.5rem 0;
}

.merges-modal__list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.merges-modal__item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  background: $color-surface-alt;
  border: 1px solid $color-border;
  border-radius: 0.75rem;
  padding: 0.9rem 1rem;
}

.merges-modal__item-users {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  flex: 1;
  min-width: 0;
}

.merges-modal__item-user {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;

  i {
    font-size: 0.9rem;
    color: $color-text-subtle;
    flex-shrink: 0;
  }

  &.is-primary i { color: $color-primary; }
}

.merges-modal__item-name {
  font-weight: 600;
  color: $color-text;
  white-space: nowrap;
}

.merges-modal__item-email {
  color: $color-text-muted;
  font-size: 0.8rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.merges-modal__primary-tag {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: $color-primary-dark;
  background: $color-primary-soft;
  padding: 0.1rem 0.4rem;
  border-radius: $radius-pill;
  flex-shrink: 0;
}

.merges-modal__delete-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: transparent;
  color: $color-danger;
  border: 1px solid $color-danger;
  border-radius: 0.5rem;
  padding: 0.4rem 0.75rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background 0.15s;

  i { font-size: 0.9rem; }

  &:hover:not(:disabled) { background: rgba($color-danger, 0.07); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}
</style>
